/**
 * Drawer — 오른쪽 슬라이드 패널 어댑터 (shadcn Sheet 위임)
 *
 * 기존 public API를 완전 보존하면서 shadcn Sheet 컴포넌트로 위임.
 * - open/onClose → Sheet open/onOpenChange
 * - size/width → SheetContent style(width, maxWidth)
 * - closeOnBackdrop → onInteractOutside 방지
 * - closeOnEscape → onEscapeKeyDown 방지
 * - showCloseButton → SheetContent 기본 X 버튼
 * - title → SheetHeader/SheetTitle (title 없으면 미렌더)
 * - duration prop은 shadcn 자체 애니메이션 사용으로 무시
 */

import { ReactNode } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

interface DrawerProps {
  /** 드로어 열림 여부 */
  open: boolean
  /** 닫기 요청 콜백 */
  onClose: () => void
  /** 헤더 타이틀 텍스트 */
  title?: string
  /** 드로어 너비 변형. 기본값 md (sm=360 md=480 lg=600) */
  size?: 'sm' | 'md' | 'lg'
  /** 커스텀 너비. 숫자(px) 또는 문자열(%, rem, CSS 함수 등). size보다 우선 */
  width?: number | string
  /** 슬라이드 애니메이션 지속시간(ms). shadcn 어댑터에서는 무시됨 */
  duration?: number
  /** 백드롭 클릭 시 닫기. 기본값 true */
  closeOnBackdrop?: boolean
  /** ESC 키 닫기. 기본값 true */
  closeOnEscape?: boolean
  /** X 닫기 버튼 표시. 기본값 true */
  showCloseButton?: boolean
  className?: string
  children: ReactNode
}

/** size prop → 픽셀 너비 매핑 */
const SIZE_WIDTHS: Record<'sm' | 'md' | 'lg', string> = {
  sm: '360px',
  md: '480px',
  lg: '600px',
}

export function Drawer({
  open,
  onClose,
  title,
  size = 'md',
  width,
  duration: _duration, // 미사용 — shadcn이 자체 애니메이션 관리
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className,
  children,
}: DrawerProps) {
  // width prop 우선, 없으면 size 매핑
  const resolvedWidth =
    width != null
      ? typeof width === 'number'
        ? `${width}px`
        : width
      : SIZE_WIDTHS[size]

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <SheetContent
        side="right"
        showCloseButton={showCloseButton}
        className={cn('gap-0', className)}
        style={{ width: resolvedWidth, maxWidth: resolvedWidth }}
        onInteractOutside={closeOnBackdrop ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={closeOnEscape ? undefined : (e) => e.preventDefault()}
      >
        {title && (
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
        )}
        {/* 본문: 헤더는 고정, 본문만 스크롤 + 좌우 패딩 (이전 자체 Drawer의 .body 재현) */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </SheetContent>
    </Sheet>
  )
}
